import React, { useState, useContext, useEffect, useCallback } from 'react'
import {
  AddButton,
  AddButtonImg,
  AddButtonText,
  AddProductContainer,
  BreadCrumble,
  Button,
  Card,
  CollapsibleHeader,
  FirstText,
  FormGrid,
  FormGroup,
  InfoLink,
  InfoName,
  InfoValue,
  InputText,
  Label,
  MoreInformation,
  SecondText,
  Select,
  Separator,
  ShortLabel, // Importado para uso no ShortLabel
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  Title,
} from './style'
import { ChevronDown, ChevronUp, Info } from 'lucide-react'
import plus from '../../../../assets/fashion/TableFashionHeader/plus.svg'
import AddProductForm from '../AddProductForm'
import { useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { ActionsDiv, ProductDiv, ProductShort } from '../AddProductForm/style'
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'; // Importar para o resumo do produto
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg'; // Importar para o resumo do produto


// Interfaces para os dados do produto e da API (revisadas para consistência com PedidoForm)
interface FamilyData {
  id: number;
  name: string;
  groups?: GroupData[];
  colors?: { name: string }[];
}

interface GroupData {
  id: number;
  name: string;
  products?: any[];
}

interface FashionOrderProductItemInput {
  colorId: number;
  size: string;
  quantity: number;
}

interface ProductApiData {
  fashionProductId: number;
  discount: number | null;
  totalItems: number; // Somatório das quantidades de todos os items (cores/tamanhos)
  subtotal: number; // Subtotal calculado com base em totalItems e discount
  items: FashionOrderProductItemInput[]; // Array de itens de cor/tamanho

  // Propriedades para exibição/preenchimento no frontend ou retorno da API
  fashionOrderId?: number;
  fashionProduct?: { // 'fashionProduct' pode ser opcional ou sempre presente dependendo do contexto
    id: number;
    name: string;
    price: number;
    size: string[]; // Ex: ["34", "36", "38"]
    group: { id: number; name?: string; family?: { id: number; name?:string; collectionId?: number; } };
  };
  colorCount?: number; // Este campo é definido no frontend (PedidoForm) e enviado. Usado para display.
  colors?: string[]; // Array de Nomes das cores para exibição no frontend (ex: ["Azul", "Verde"])
  // numberOfColorsToDisplay: number; // Este campo não precisa existir na interface ProductApiData, é uma variável local
  family?: { id: number; Corileone?: { name: string }; name?: string; familyId?: number; };
  group?: { id: number; name?: string };
  name?: string; // Para compatibilidade
  price?: number; // Para compatibilidade
  totalQuantity?: number; // Para compatibilidade (mesmo que totalItems)
  totalSubtotal?: number; // Para compatibilidade (mesmo que subtotal)
}


type FashionFormContent = {
  handleSave: React.MutableRefObject<() => void>
}

interface CompanyData {
  id: number
  companyName: string
}

const AddFashionOrder = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [isInformationOpen, setIsInformationOpen] = useState(false)
  const [ companyData, setCompanyData ] = useState<CompanyData[]>([])
  const [supplierData, setSupplierData ] = useState<CompanyData[]>([])
  const { handleSave } = useOutletContext<FashionFormContent>()
  // Usamos ProductApiData[] para garantir a consistência dos tipos
  const [ addedProducts, setAddedProducts ] = useState<ProductApiData[]>([]) 
  const [formState, setFormState] = useState({
    supplier: '',
    seller: '',
    company: '',
    orderType: '',
    paymentCondition: '',
    typeOfShipping: '',
    trackingCode: '',
    attachment: '',
    finalDateOfDelivery: '',
    startDateOfDelivery: '',
    extraInformation: '',
    quantityItems: '0',
    quantityPieces: '0',
    totalCost: 'R$ 0,00',
    deliveryTime: '',
    shippingCost: '',
    maximumBillingAmount: '',
  })
  
  const cleanCurrency = (value: any): number => {
    const stringValue = String(value || '');
    const number = Number(stringValue.replace(/[^0-9,-]+/g,"").replace(',', '.'))
    return isNaN(number) ? 0 : number
  }

  const formatDateToISO = (dateString: string): string | null => {
    if (!dateString) {
      return null;
    }
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return `${year}-${month}-${day}T00:00:00.000Z`;
    }
    return null;
  };

  const handleSaveOrder = useCallback(async () => {
    try {
      const formattedStartDate = formatDateToISO(formState.startDateOfDelivery);
      const formattedFinalDate = formatDateToISO(formState.finalDateOfDelivery);

      const payload = {
        company: {
          connect: { id: Number(formState.company) }
        },
        supplier: {
          connect: { id: Number(formState.supplier) }
        },
        seller: {
          connect: { id: Number(formState.seller) }
        },
        orderType: formState.orderType,
        paymentCondition: formState.paymentCondition,
        typeOfShipping: formState.typeOfShipping,
        trackingCode: formState.trackingCode,
        shippingCost: cleanCurrency(formState.shippingCost),
        totalCost: cleanCurrency(formState.totalCost),
        startDateOfDelivery: formattedStartDate,
        finalDateOfDelivery: formattedFinalDate,
        extraInformation: formState.extraInformation,
        quantityItems: formState.quantityItems,
        quantityPieces: formState.quantityPieces,
        deliveryTime: formState.deliveryTime,
        attachments: formState.attachment ? formState.attachment.split(',') : [],
        status: 'Aguardando pagamento',
        saleStatus: 'Em andamento',
        maximumBillingAmount: formState.maximumBillingAmount,
        products: addedProducts.map(product => ({
          fashionProductId: product.fashionProduct?.id || product.fashionProductId,
          // Garante que o groupId seja acessado corretamente, da estrutura aninhada
          groupId: product.fashionProduct?.group?.id || product.group?.id, 
          discount: product.discount,
          totalItems: product.totalItems,
          subtotal: product.subtotal,
          items: product.items, // Envia o array de items para o backend
          // colorCount e sizeQuantities não são mais enviados diretamente para FashionOrderProduct
        }))
      };

      console.log('Payload enviado:', payload); // Mantenha este log para verificar

      const response = await fetch(`${process.env.REACT_APP_FASHION_ORDERS_BASE_URL}/fashion-order/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro ao salvar pedido:', errorText);
        alert(`Erro ao salvar pedido: ${errorText}`); // Alerta o erro para o usuário
        return;
      }

      const result = await response.json();
      console.log('Pedido criado com sucesso:', result);
      alert('Pedido criado com sucesso!'); // Feedback de sucesso
      // Opcional: Redirecionar ou limpar o formulário após o sucesso
    } catch (error) {
      console.error('Erro geral ao salvar pedido:', error);
      alert('Erro geral ao salvar pedido. Verifique o console para mais detalhes.');
    }
  }, [formState, addedProducts, cleanCurrency, formatDateToISO]);


  // Use useEffect para injetar a função no contexto
  useEffect(() => {
    handleSave.current = handleSaveOrder
  }, [handleSaveOrder])

  const fetchData = async () => {
    try {
      const response = await axios.get(`https://companies-260311756054.southamerica-east1.run.app/companies?filters[0]=1&filters[1]=2`);
      const data = response.data;
  
      // Verifica o array companyTypes dentro de cada item
      const clientes = data.filter((item: any) => item.companyTypes?.includes('Cliente'));
      const fornecedores = data.filter((item: any) => item.companyTypes?.includes('Fornecedor'));
  
      setCompanyData(clientes);
      setSupplierData(fornecedores);

    } catch (error: any) {
      console.error('Erro ao buscar dados:', error.message);
    }
  };
  
  useEffect(() => {
      fetchData(); // Carrega dados de fornecedor ou cliente dependendo do estado
  }, []); // Vazio para carregar apenas uma vez ao montar o componente

  // Novo useEffect para calcular os valores dinamicamente
  useEffect(() => {
    let totalItemsCount = 0; // Contagem de produtos únicos
    let totalPiecesSum = 0; // Soma de todas as peças (totalItems de cada produto)
    let totalCostSum = 0; // Soma de todos os subtotais

    addedProducts.forEach(product => {
      totalItemsCount += 1;
      // Log para inspecionar o valor de product.totalItems
      console.log(`Produto: ${product.fashionProduct?.name || product.name || 'N/A'}, totalItems para soma: ${product.totalItems}, tipo: ${typeof product.totalItems}`);
      totalPiecesSum += Number(product.totalItems || 0);
      totalCostSum += product.subtotal || 0;
    });
    
    // Logs para depuração
    console.log('AddFashionOrder - Calculando Totais:');
    console.log('   Produtos no addedProducts:', addedProducts);
    console.log('   totalItemsCount:', totalItemsCount);
    console.log('   totalPiecesSum (antes de setFormState):', totalPiecesSum); // Log antes de setar o estado
    console.log('   totalCostSum:', totalCostSum);

    setFormState(prevState => ({
      ...prevState,
      quantityItems: String(totalItemsCount),
      quantityPieces: String(totalPiecesSum),
      totalCost: `R$ ${totalCostSum.toFixed(2).replace('.', ',')}`,
    }));
  }, [addedProducts, cleanCurrency, setFormState]); // cleanCurrency e setFormState são dependências corretas aqui

  const handleRemoveProductParent = (indexToRemove: number) => {
    setAddedProducts(prevProducts => {
      const newProducts = [...prevProducts];
      newProducts.splice(indexToRemove, 1);
      return newProducts;
    });
  };

  // Função para adicionar um novo produto ao array addedProducts
  const handleNewProductAdded = (newProduct: ProductApiData) => {
    console.log('AddFashionOrder - handleNewProductAdded (ADD) chamado!');
    console.log('AddFashionOrder - newProduct recebido do PedidoForm:', newProduct);
    setAddedProducts(prevProducts => [...prevProducts, newProduct]); // Adiciona o novo produto
    setShowAddProduct(false); // Fecha o formulário de adição
    console.log('AddFashionOrder - addedProducts atualizado (adição):', [...addedProducts, newProduct]);
  };

  const handleProductUpdatedInAddContext = (oldProduct: any, updatedProduct: any) => {
    // Esta função é um placeholder. No contexto de "Adicionar Pedido",
    // onProductUpdated não é tipicamente usado para *edição* de itens já adicionados,
    // mas pode ser chamado pelo AddProductForm se ele não souber que está em modo "Adicionar".
    // Se o AddProductForm está apenas para adicionar e não para editar *nesta tela*,
    // esta função não será necessária, ou pode apenas logar um aviso.
    console.warn('AddFashionOrder - onProductUpdated foi chamado em contexto de adição. Isso não deveria acontecer.');
    // Se você permitir edição de produtos *na própria tela de criação*, implemente aqui.
    // Caso contrário, certifique-se de que o AddProductForm não chame onProductUpdated
    // quando isEditing é false (o que já deve estar acontecendo).
  };


  return (
    <>
      <BreadCrumble>
        <FirstText>Pedidos fashion</FirstText>
        <SecondText>&gt; Novo pedido</SecondText>
      </BreadCrumble>
      <Card>
        <FormGrid>
          <FormGroup>
            <Label>Fornecedor</Label>
            <Select
              value={formState.supplier}
              onChange={(e) => setFormState({ ...formState, supplier: e.target.value })}
            >
              <option value="">Selecione</option>
              {supplierData.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.companyName}
                </option>
              ))}
              <option value="10">FromSoftware</option>
            </Select>
            <InfoLink>
              <Info size={16} />
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span onClick={() => setIsInformationOpen(!isInformationOpen)}>
                  Mais informações
                </span>
                {isInformationOpen && (
                  <MoreInformation>
                    <InfoName>Fantasia: <InfoValue>Super Fornecedor</InfoValue></InfoName>
                    <InfoName>CNPJ: <InfoValue>12.345.678/0001-10</InfoValue></InfoName>
                    <InfoName>Contato: <InfoValue>José da Silva</InfoValue></InfoName>
                    <InfoName>Cidade/UF: <InfoValue>São Paulo/SP</InfoValue></InfoName>
                    <InfoName>Telefone: <InfoValue>(12)98765-4321</InfoValue></InfoName>
                    <InfoName>Email: <InfoValue>email@email.com</InfoValue></InfoName>
                  </MoreInformation>
                )}
              </div>
            </InfoLink>
          </FormGroup>
          <FormGroup>
            <Label>Vendedor</Label>
            <Select
              value={formState.seller}
              onChange={(e) => setFormState({ ...formState, seller: e.target.value })}
            >
              <option value="">Selecione</option>
              <option value="1">Vendedor A</option>
            </Select>
            <InfoLink>
              <Info size={16} />
              <span>Mais infomações</span>
            </InfoLink>
          </FormGroup>
            <FormGroup>
            <Label>Cliente</Label>
            <Select
              value={formState.company}
              onChange={(e) => setFormState({ ...formState, company: e.target.value })}
            >
              <option value="">Selecione</option>
              {companyData.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.companyName}
                </option>
              ))}
              <option value="10">FromSoftware</option>
            </Select>
              <InfoLink>
                <Info size={16} />
                <span>Mais informações</span>
              </InfoLink>
            </FormGroup>
        </FormGrid>

        <Separator />

        <CollapsibleHeader onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <Title>Detalhes do pedido</Title>
          <Button>
            {isDetailsOpen ? (
              <ChevronUp size={12} />
            ) : (
              <ChevronDown size={12} />
            )}
          </Button>
        </CollapsibleHeader>

        {isDetailsOpen && (
          <>
            <FormGrid style={{ marginTop: '16px' }}>
              <FormGroup>
                <Label>Tipo de pedido</Label>
                <Select
                  value={formState.orderType}
                  onChange={(e) => setFormState({ ...formState, orderType: e.target.value })}
                >
                  <option value="">Selecione</option>
                  <option value="Venda">Venda</option>
                  <option value="Reposição">Reposição</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Data inicio da entrega</Label>
                <InputText
                  type="text"
                  value={formState.startDateOfDelivery}
                  onChange={(e) => setFormState({ ...formState, startDateOfDelivery: e.target.value })}
                  className="w-full rounded bg-gray-100 p-2" // Opcional: para manter o estilo do Select
                />
              </FormGroup>

              <FormGroup>
                <Label>Data final da entrega</Label>
                <InputText
                  type="text"
                  value={formState.finalDateOfDelivery}
                  onChange={(e) => setFormState({ ...formState, finalDateOfDelivery: e.target.value })}
                  className="w-full rounded bg-gray-100 p-2" // Opcional: para manter o estilo do Select
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Condição de pagamento</Label>
                <Select
                  value={formState.paymentCondition}
                  onChange={(e) => setFormState({ ...formState, paymentCondition: e.target.value })}
                >
                  <option value="">Selecione</option>
                  <option value="30 dias">30 dias</option>
                </Select>
              </FormGroup>

              <FormGroup>
              <Label>Tipo de frete</Label>
              <Select
                value={formState.typeOfShipping}
                onChange={(e) => setFormState({ ...formState, typeOfShipping: e.target.value })}
              >
                <option value="">Selecione</option>
                <option value="FOB">FOB</option>
                <option value="CIF">CIF</option>
              </Select>
              </FormGroup>

              <FormGroup>
              <Label>Valor do frete</Label>
              <Select
                value={formState.shippingCost}
                onChange={(e) => setFormState({ ...formState, shippingCost: e.target.value })}
              >
                <option value="">Selecione</option>
                <option value="0">R$ 0,00</option>
                <option value="50">R$ 50,00</option>
                <option value="100">R$ 100,00</option>
              </Select>
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
              <Label>Código de rastreio</Label>
              <Select
                value={formState.trackingCode}
                onChange={(e) => setFormState({ ...formState, trackingCode: e.target.value })}
              >
                <option value="">Selecione</option>
                <option value="TRK123456">TRK123456</option>
                <option value="TRK987654">TRK987654</option>
              </Select>
              </FormGroup>

              <FormGroup>
              <Label>Anexo</Label>
              <Select
                value={formState.attachment}
                onChange={(e) => setFormState({ ...formState, attachment: e.target.value })}
              >
                <option value="">Selecione</option>
                <option value="arquivo.pdf">arquivo.pdf</option>
                <option value="imagem.jpg">imagem.jpg</option>
              </Select>
              </FormGroup>

              <FormGroup>
              <Label>Valor máximo de faturamento</Label>
              <InputText
                type="text"
                value={formState.maximumBillingAmount}
                onChange={(e) => setFormState({ ...formState, maximumBillingAmount: e.target.value })}
                className="w-full rounded bg-gray-100 p-2"
                placeholder="Digite o valor máximo"
              />
              </FormGroup>
            </FormGrid>
            <FormGrid>
              <FormGroup>
                <Label>Informações adicionais</Label>
                <InputText
                  type="text"
                  value={formState.extraInformation}
                  onChange={(e) => setFormState({ ...formState, extraInformation: e.target.value })}
                />
              </FormGroup>
            </FormGrid>
          </>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel>Quantidade de itens:</SummaryLabel>
            <SummaryValue>{formState.quantityItems}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de peças:</SummaryLabel>
            <SummaryValue>{formState.quantityPieces}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total:</SummaryLabel>
            <SummaryValue>{formState.totalCost}</SummaryValue>
          </SummaryItem>
        </Summary>
      </Card>
      
      {/* Resumo dos produtos adicionados (agora visível na criação de pedido) */}
      {Array.isArray(addedProducts) && addedProducts.length > 0 && (
          <>
            {addedProducts.map((product, index) => {
              // LÓGICA PARA OBTER OS NOMES DE FAMÍLIA E GRUPO DE FORMA CONSISTENTE
              let familyName = '-';
              if (product.fashionProduct?.group?.family?.name) {
                familyName = product.fashionProduct.group.family.name;
              } else if (product.family?.name) { 
                familyName = product.family.name;
              }

              let groupName = '-';
              if (product.fashionProduct?.group?.name) {
                groupName = product.fashionProduct.group.name;
              } else if (product.group?.name) { 
                groupName = product.group.name;
              }

              // USE product.colorCount PARA EXIBIR O NÚMERO DE CORES
              // O campo colorCount é o que o PedidoForm está calculando e enviando
              const numberOfColorsToDisplay = product.colorCount !== undefined && product.colorCount !== null
                                               ? product.colorCount
                                               : (product.items && Array.isArray(product.items)
                                                   ? new Set(product.items.map(item => item.colorId)).size
                                                   : 0);

              const productFields = [
                { label: 'Produto', value: product.fashionProduct?.name ?? '-', showIcon: true },
                { label: 'Episódio', value: familyName, showIcon: true },
                { label: 'Cores', value: numberOfColorsToDisplay, showIcon: true }, // <--- AQUI ESTÁ A MUDANÇA
                { label: 'Precificação', value: (typeof product.fashionProduct?.price === 'number' ? `R$ ${product.fashionProduct.price.toFixed(2).replace('.', ',')}` : '-') , showIcon: true },
                { label: 'Quantidade de peças', value: product.totalItems ?? '-', showIcon: true },
                { label: 'Subtotal', value: (typeof product.subtotal === 'number' ? `R$ ${product.subtotal.toFixed(2).replace('.', ',')}` : '-') , showIcon: false },
              ];

              return (
                <Card key={product.fashionProductId || index}> {/* Use ID ou index para a key */}
                  <ProductShort>
                    <ProductDiv> {/* Reutiliza ProductDiv do AddProductForm/style */}
                    {productFields.map(({ label, value, showIcon }, i) => (
                      <ShortLabel key={i}>
                        <span style={{ fontWeight: 'normal' }}>{label}:</span> {value} {showIcon && <img style={{ marginLeft: '0.5rem'}} src={rectangle} alt="" />}
                      </ShortLabel>
                    ))}

                    <ActionsDiv className="flex items-center gap-2">
                      <img
                        src={trash}
                        alt="Remover"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRemoveProductParent(index)} // Chama a função do pai para remover
                      />
                      {/* Na tela de criação, não há um botão para "editar" individualmente o produto adicionado via formulário expandido */}
                      {/* Então, o botão de expandir/colapsar o formulário de edição de produto não se aplica aqui. */}
                      {/* Se você quiser permitir a edição de produtos *já adicionados* aqui, precisaria de lógica similar ao EditFashionOrder */}
                    </ActionsDiv>
                    </ProductDiv>
                    </ProductShort>
                  </Card>
                );
              })}
            </>
          )}


        <AddProductContainer>
          {showAddProduct && (
              <AddProductForm 
                order={[]}
                setOrder={() => {}}
                // Removidas 'order' e 'setOrder' para este componente
                // Elas eram usadas para o AddProductForm gerenciar a lista localmente,
                // mas agora a lista é gerenciada pelo AddFashionOrder via callbacks.
                onRemoveProduct={handleRemoveProductParent} // Ainda é necessário para o botão de "Remover" dentro do formulário de edição (se ativado)
                onProductAdded={handleNewProductAdded} // ESSA É A NOVA PROP PARA ADICIONAR PRODUTOS
                onProductUpdated={handleProductUpdatedInAddContext} // Placeholder, não deve ser chamado para adição
                isEditing={false} // Sempre false para a criação de um novo produto
              />
          )}
          <AddButton
            onClick={() => {
              setShowAddProduct(true)
            }}
          >
            <AddButtonImg src={plus} />
            <AddButtonText>Adicionar produto</AddButtonText>
          </AddButton>
        </AddProductContainer>
      </>
    )
}

export default AddFashionOrder
